import { AxiosResponse } from "axios";

import apiClient from "../../../shared/infrastructure/api/client";
import type { CentreRates, PayRates, PractitionerRates } from "../domain/PayRates";
import type {
    CourseOverview,
    PractitionerReport,
    ReportCourseOption,
    StatementFormat,
    ReportQueryParams,
    ReportScope,
    ReportSubjectKind,
    ReportSubjectOption,
} from "../domain/PractitionerReport";

/** Where a scope's report lives: the caller's own, or a member's under the centre. */
export function reportBase(scope: ReportScope): string {
    switch (scope.kind) {
        case "self":
            return "/reports/practitioner";
        case "member":
            return `/centers/${scope.centerId}/reports/practitioners/${scope.therapistId}`;
        case "centre":
            return `/centers/${scope.centerId}/reports/centre`;
        case "course":
            return `/centers/${scope.centerId}/reports/courses/${scope.courseId}`;
    }
}

/** One month (`month=YYYY-MM`) or one whole year (`year=YYYY`) — never both. */
export interface StatementParams {
    month?: string;
    year?: string;
    course_id?: string;
    /** A report across professionals narrowed to one of them. */
    therapist_id?: string;
    locale: string;
    subject_type?: ReportSubjectKind;
    subject_id?: string;
}

export const ReportsAPI = {
    report: (
        scope: ReportScope,
        params: ReportQueryParams
    ): Promise<AxiosResponse<PractitionerReport>> => apiClient.get(reportBase(scope), { params }),

    /** The courses of the scope's sessions in the window — one student's, when named. */
    courses: (
        scope: ReportScope,
        params: Pick<ReportQueryParams, "start" | "end" | "subject_type" | "subject_id">
    ): Promise<AxiosResponse<ReportCourseOption[]>> =>
        apiClient.get(`${reportBase(scope)}/courses`, { params }),

    /** A training centre's courses over the window, across their mentors. */
    courseOverview: (
        centerId: string,
        params: Pick<ReportQueryParams, "start" | "end">
    ): Promise<AxiosResponse<CourseOverview>> =>
        apiClient.get(`/centers/${centerId}/reports/courses`, { params }),

    subjects: (
        scope: ReportScope,
        params: Pick<ReportQueryParams, "start" | "end">
    ): Promise<AxiosResponse<ReportSubjectOption[]>> =>
        apiClient.get(`${reportBase(scope)}/subjects`, { params }),

    /** The month's or the year's statement, as the PDF or workbook the backend makes. */
    statement: (
        scope: ReportScope,
        params: StatementParams,
        format: StatementFormat = "pdf"
    ): Promise<AxiosResponse<Blob>> =>
        apiClient.get(`${reportBase(scope)}/statement.${format}`, { params, responseType: "blob" }),

    myRates: (): Promise<AxiosResponse<PractitionerRates>> => apiClient.get("/reports/pay-rates"),

    setMyRates: (rates: PayRates): Promise<AxiosResponse<PayRates>> =>
        apiClient.put("/reports/pay-rates", rates),

    centreRates: (centerId: string): Promise<AxiosResponse<CentreRates>> =>
        apiClient.get(`/centers/${centerId}/pay-rates`),

    setCentreRates: (centerId: string, rates: PayRates): Promise<AxiosResponse<PayRates>> =>
        apiClient.put(`/centers/${centerId}/pay-rates`, rates),

    setMemberRates: (
        centerId: string,
        therapistId: string,
        rates: PayRates
    ): Promise<AxiosResponse<PayRates>> =>
        apiClient.put(`/centers/${centerId}/pay-rates/therapists/${therapistId}`, rates),
};
